// =========================================================================
// Grant-contact-access form schema + the access-level narrowing.
// Split out of GrantClientAccessModal.tsx (module-size).
// =========================================================================

import { z } from "zod";
import { ACCESS_LEVEL_OPTIONS } from "@/lib/auth/access-levels";
import type { AccessLevel } from "@/lib/types/database";

// Access-level option metadata (labels + ACCURATE descriptions) is the single
// typed source of truth in lib/auth/access-levels.ts — kept in lockstep with the
// RLS migration 20260619130000_client_access_levels_ticket_create_gate.sql.
//   full      = company-wide data + can create tickets
//   limited   = single granted contact only + can create tickets
//   read_only = company-wide VIEW like full, but CANNOT create tickets
export const accessLevels = ACCESS_LEVEL_OPTIONS;

// The form validates ONLY the fields the user actually enters. `userId` is NOT
// a form field: it is a required identifier supplied by the caller (the
// `userId` / `user` prop) or picked via UserSelect, and is passed straight to
// the mutation. Keeping a prop-derived required id OUT of the schema removes the
// reset-timing race where a late-arriving prop left a hidden `userId` field
// empty and blocked submit. (tsc can't catch that: "" is a valid string and
// "required" is a runtime zod check.)
export const grantContactAccessSchema = z.object({
  contact_id: z.string().min(1, "Contact is required"),
  access_level: z
    .enum(["read_only", "limited", "full"])
    .refine((val) => val, "Access level is required"),
});

export type GrantContactAccessForm = z.infer<typeof grantContactAccessSchema>;

/**
 * Narrow a <select> value to an AccessLevel. USER_ROLES_AND_ACCESS: the level
 * is stored on the grant row and read by RLS, so an unrecognised string must
 * never be written — this returns null instead of asserting with `as`.
 */
export function toAccessLevel(value: string): AccessLevel | null {
  return accessLevels.some((level) => level.value === value)
    ? (value as GrantContactAccessForm["access_level"])
    : null;
}

/** The helper text under the level <select>, for the currently-selected level. */
export function accessLevelDescription(value: string): string | undefined {
  return accessLevels.find((level) => level.value === value)?.description;
}
